import { supabase } from '@/lib/supabaseClient';
import { DocumentRecord, DocumentType, EntityType } from '@/types/database';

export const ATTACHMENTS_BUCKET = 'vsb-attachments';

export interface UploadDocumentParams {
  file: File | Blob;
  fileName: string;
  entityType: EntityType;
  entityId: string;
  documentType: DocumentType;
  notes?: string;
  folderCategory?: 'weighing-sheets' | 'invoices' | 'settlement-pdfs' | 'health-certs' | 'receipts' | 'general';
}

/**
 * Service for private document attachment management using Supabase Storage ('vsb-attachments')
 * and the polymorphic 'documents' table.
 */
export const documentService = {
  /**
   * Derives default storage folder category from document type.
   */
  getFolderCategory(documentType: DocumentType): string {
    switch (documentType) {
      case 'weighing_sheet':
        return 'weighing-sheets';
      case 'invoice':
      case 'delivery_receipt':
        return 'invoices';
      case 'settlement_pdf':
        return 'settlement-pdfs';
      case 'lab_result':
        return 'health-certs';
      case 'photo':
        return 'general';
      default:
        return 'general';
    }
  },

  /**
   * Uploads a file to private 'vsb-attachments' bucket and links metadata into 'documents' table.
   */
  async uploadDocument(params: UploadDocumentParams): Promise<DocumentRecord> {
    const { file, fileName, entityType, entityId, documentType, notes } = params;
    const folder = params.folderCategory || this.getFolderCategory(documentType);

    // Sanitize file name and create unique path
    const sanitizedName = fileName.replace(/[^a-zA-Z0-9._-]/g, '_');
    const timestamp = Date.now();
    const storagePath = `${folder}/${entityType}/${entityId}/${timestamp}_${sanitizedName}`;

    const mimeType = file.type || 'application/octet-stream';
    const fileSize = file.size;

    // 1. Upload to Supabase Storage private bucket
    const { error: storageError } = await supabase.storage
      .from(ATTACHMENTS_BUCKET)
      .upload(storagePath, file, {
        contentType: mimeType,
        upsert: false,
      });

    if (storageError) {
      console.error('[documentService] Storage upload error:', storageError);
      throw new Error(`Failed to upload file to storage: ${storageError.message}`);
    }

    // 2. Insert metadata into 'documents' database table
    const { data: userSession } = await supabase.auth.getSession();
    const userId = userSession.session?.user?.id || null;

    const { data: docRecord, error: dbError } = await supabase
      .from('documents')
      .insert({
        entity_type: entityType,
        entity_id: entityId,
        document_type: documentType,
        file_path: storagePath,
        file_name: fileName,
        file_size: fileSize,
        mime_type: mimeType,
        notes: notes || null,
        created_by: userId,
        updated_by: userId,
        is_void: false,
      })
      .select('*')
      .single();

    if (dbError) {
      console.error('[documentService] Database insert error:', dbError);
      // Attempt storage rollback to avoid orphaned file
      await supabase.storage.from(ATTACHMENTS_BUCKET).remove([storagePath]);
      throw new Error(`Failed to record document metadata in database: ${dbError.message}`);
    }

    return docRecord as DocumentRecord;
  },

  /**
   * Generates a secure, short-lived signed URL to preview or download a private file.
   * Default expiration: 300 seconds (5 minutes).
   */
  async getSignedUrl(filePath: string, expiresInSeconds = 300): Promise<string> {
    const { data, error } = await supabase.storage
      .from(ATTACHMENTS_BUCKET)
      .createSignedUrl(filePath, expiresInSeconds);

    if (error || !data?.signedUrl) {
      console.error('[documentService] Signed URL creation error:', error);
      throw new Error(`Failed to create secure access URL: ${error?.message || 'Unknown error'}`);
    }

    return data.signedUrl;
  },

  /**
   * Fetches all active (non-voided) documents linked to a specific entity.
   */
  async getEntityDocuments(entityType: EntityType, entityId: string): Promise<DocumentRecord[]> {
    const { data, error } = await supabase
      .from('documents')
      .select('*')
      .eq('entity_type', entityType)
      .eq('entity_id', entityId)
      .eq('is_void', false)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('[documentService] Fetch entity documents error:', error);
      throw new Error(`Failed to fetch documents: ${error.message}`);
    }

    return (data || []) as DocumentRecord[];
  },

  /**
   * Fetches all documents (including soft-voided for audit views).
   */
  async getAllDocuments(limit = 100): Promise<DocumentRecord[]> {
    const { data, error } = await supabase
      .from('documents')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('[documentService] Fetch all documents error:', error);
      throw new Error(`Failed to list documents: ${error.message}`);
    }

    return (data || []) as DocumentRecord[];
  },

  /**
   * Soft-voids a document record with a mandatory void reason.
   * Preserves the physical file in storage for non-destructive audit compliance.
   */
  async softVoidDocument(documentId: string, voidReason: string): Promise<void> {
    if (!voidReason || voidReason.trim().length === 0) {
      throw new Error('Mandatory void reason is required.');
    }

    const { data: userSession } = await supabase.auth.getSession();
    const userId = userSession.session?.user?.id || null;

    const { error } = await supabase
      .from('documents')
      .update({
        is_void: true,
        void_reason: voidReason.trim(),
        voided_at: new Date().toISOString(),
        voided_by: userId,
        updated_by: userId,
        updated_at: new Date().toISOString(),
      })
      .eq('id', documentId);

    if (error) {
      console.error('[documentService] Soft-void error:', error);
      throw new Error(`Failed to void document: ${error.message}`);
    }
  },
};
